import React from 'react';
import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';
import { Icon } from '../Icon';
import ToastContent from './ToastContent';

interface IToastContainerProps {
  appearance: 'error' | 'info' | 'success' | 'warning';
  children?: React.ReactNode;
}

const Layout = styled.div<Partial<IToastContainerProps>>`
  position: relative;
  display: flex;
  flex-shrink: 0;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  padding: ${toRem(theme.space.lg)} ${toRem(theme.space.xl)};
  border-radius: ${toRem(5.5)};
  overflow: visible;

  ${({ appearance }) =>
    appearance === 'success' &&
    css`
      border-left: ${toRem(5.5)} solid ${theme.color.success[50]};
    `}

  ${({ appearance }) =>
    appearance === 'error' &&
    css`
      border-left: ${toRem(5.5)} solid ${theme.color.danger[50]};
    `}

    ${({ appearance }) =>
    appearance === 'warning' &&
    css`
      border-left: ${toRem(5.5)} solid ${theme.color.warning[50]};
    `}
`;

const StyledIcon = styled(Icon).attrs({ width: 22, height: 22 })`
  margin-right: ${toRem(theme.space.lg)};
`;

// No animation or dismissal timer here — the keyframes live in ToastStyles.tsx and react-toastify
// only decides when they run.
const ToastContainer: React.FC<IToastContainerProps> = ({ appearance, children, ...props }) => (
  <Layout appearance={appearance} {...props}>
    {appearance === 'error' && <StyledIcon icon="Error" fill={theme.color.danger[50]} />}
    {appearance === 'success' && <StyledIcon icon="Success" fill={theme.color.success[50]} />}
    {appearance === 'warning' && <StyledIcon icon="Error" fill={theme.color.warning[50]} />}
    {children}
  </Layout>
);

export const SuccessToastContainer: React.FC<{
  title?: string;
  message: string;
  dataCy?: string;
}> = ({ title = 'Success', message, dataCy = 'success-toast' }) => (
  <ToastContainer appearance="success" data-cy={dataCy}>
    <ToastContent title={title} message={message} />
  </ToastContainer>
);

export const FailToastContainer: React.FC<{
  title?: string;
  message: string;
}> = ({ title = 'Fail', message }) => (
  <ToastContainer data-cy="fail-toast" appearance="error">
    <ToastContent title={title} message={message} />
  </ToastContainer>
);

/**
 * Toast shown when the backend rejects a gated request with 409 `client_capabilities_required`
 * It surfaces the server's "please refresh" message and offers a Refresh action that
 * reloads the page so the user picks up the new bundle. We let the user trigger the reload rather
 * than reloading automatically so any unsaved input isn't discarded out from under them.
 */
export const RefreshToastContainer: React.FC<{
  title?: string;
  message: string;
  onRefresh?: () => void;
}> = ({ title = 'Refresh required', message, onRefresh = () => window.location.reload() }) => (
  <ToastContainer data-cy="refresh-toast" appearance="warning">
    <ToastContent title={title} message={message} actionLabel="Refresh" onAction={onRefresh} />
  </ToastContainer>
);

export default ToastContainer;
